import React, { useState, useRef, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

import PhotoUpload from './components/upload/PhotoUpload';
import ResultsDisplay from './components/results/ResultsDisplay';

// Create a custom theme with black and white colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#212121', // Dark gray as primary
      light: '#484848',
      dark: '#000000',
    },
    secondary: {
      main: '#757575', // Medium gray
      light: '#a4a4a4',
      dark: '#494949',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#ffa000',
    },
    info: {
      main: '#616161',
    },
    success: {
      main: '#2e7d32',
    }
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 500,
      fontSize: '1.75rem',
    },
    h2: {
      fontWeight: 500,
      fontSize: '1.5rem',
    },
    h3: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        }
      }
    }
  },
});

function App() {
  const [analysisData, setAnalysisData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const photoUploadRef = useRef(null);

  // Проверяем URL на наличие параметров data= и photo=
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const dataParam = queryParams.get('data');
    const photoParam = queryParams.get('photo');
    
    if (dataParam) {
      try {
        console.log('🔍 App: Обнаружены данные в URL параметре data');
        const decodedData = JSON.parse(decodeURIComponent(dataParam));
        setAnalysisData(decodedData);
      } catch (err) {
        console.error('❌ App: Ошибка при парсинге данных из URL:', err);
        setError('Ошибка при анализе данных из URL');
      }
    }
    
    if (photoParam) {
      console.log('🔍 App: Обнаружена ссылка на фото в URL параметре photo');
      setPhotoUrl(photoParam);
    }
  }, []);

  const handleAnalysisComplete = (data) => {
    console.log('✅ App: Получены результаты анализа');
    setAnalysisData(data);
    setIsLoading(false);
  };

  const handleReset = () => {
    console.log('🔄 App: Сброс всех данных');
    setAnalysisData(null);
    setError(null);
    setIsLoading(false);
    setPhotoUrl(null);
    // Сбрасываем фото при нажатии "Начать заново"
    if (photoUploadRef.current) {
      photoUploadRef.current.resetPhoto();
    }
    // Удаляем параметры из URL
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleError = (errorMessage) => {
    console.log('❌ App: Получена ошибка:', errorMessage);
    setError(errorMessage);
    setIsLoading(false);
  };

  // Функция для сброса результатов при загрузке новой фотографии
  const handleResetResult = () => {
    console.log('🔄 App: Сброс результатов при загрузке новой фотографии');
    setAnalysisData(null);
    setError(null);
    setIsLoading(false);
    setPhotoUrl(null);
  };

  // Логируем начало загрузки анализа
  const handleAnalysisStart = () => {
    console.log('⏳ App: Запуск анализа...');
    setIsLoading(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          py: 3,
          backgroundColor: '#f5f5f5',
        }}
      >
        <Container maxWidth="lg">
          <Box mb={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                color: '#212121',
                fontWeight: 500,
                fontSize: '1.5rem',
              }}
            >
              Takeda Diagnostic Tool
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <PhotoUpload 
                ref={photoUploadRef}
                onAnalysisStart={handleAnalysisStart}
                onAnalysisComplete={handleAnalysisComplete}
                onError={handleError}
                onResetResult={handleResetResult}
                photoUrl={photoUrl}
              />
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Paper elevation={1} sx={{ 
                p: 4, 
                height: '100%', 
                minHeight: 400, 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 2
              }}>
                <Typography 
                  variant="h2" 
                  component="h2" 
                  gutterBottom 
                  sx={{ 
                    fontSize: '1.5rem', 
                    color: 'primary.main',
                    mb: 1 
                  }}
                >
                  Результаты диагностики
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {analysisData ? 'Результаты анализа' : 'Загрузите фото, чтобы увидеть результаты диагностики'}
                </Typography>

                {(analysisData || error || isLoading) ? (
                  <ResultsDisplay 
                    data={analysisData} 
                    error={error}
                    isLoading={isLoading}
                    onReset={handleReset}
                  />
                ) : (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexGrow: 1,
                      opacity: 0.5
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box 
                        component="span" 
                        sx={{ 
                          fontSize: '3rem', 
                          display: 'block',
                          color: '#e0e0e0'
                        }}
                      >
                        i
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Нет результатов для отображения
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Пожалуйста, загрузите и проанализируйте фото
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
